import { useMemo } from 'react'
import { useFormulaRules } from './useFormulaRules'
import { useInventory } from 'hooks/inventory/useInventory'
import { Formula, FormulaRule } from '@otw/models'
import { matchFormula } from './matchFormula'
import { calculatePrice } from './calculatePrice'

export { calculatePrice }

const mileageGrid = [0, 10000, 20000, 30000, 40000, 50000, 75000, 100000, 150000, 200000, 250000]

const getYearRange = (yearRules: FormulaRule[]) => {
  const activeYearRules = yearRules.filter(r => r.status === 'active')
  const currentYear = new Date().getFullYear()
  const minYear = Math.min(...activeYearRules.map(r => r.lowerBound || Number.MAX_VALUE))
  const maxYear = Math.max(...activeYearRules.map(r => r.lowerBound || currentYear))
  const adjustedMinYear = Math.min(minYear, maxYear - 4)
  const yearRange = Math.max(maxYear - adjustedMinYear + 1, 5)
  const years = Array.from({ length: yearRange }, (_, i) => maxYear - i)
  return years
}

export const useFormula = (
  formula: Formula,
  excludedVehicleIds: string[] = [],
  dateRange?: { start: Date | null; end: Date | null },
) => {
  // Fetch rules for this formula
  const { rules, isLoading: isLoadingRules, ...restRules } = useFormulaRules({ formulaId: formula.id })
  // Fetch all vehicles
  const { vehicles, isLoading: isLoadingVehicles } = useInventory('master_inventory')

  // Filter vehicles matching this formula
  const matchingVehicles = useMemo(() => {
    if (!vehicles) return []
    return vehicles.filter(v => matchFormula([formula], v) !== null)
  }, [vehicles, formula])

  // Note: sold/soldElsewhere/totalSold now calculated in useFormulaStats to avoid duplication
  // Components should get these from formulaStat

  // Year/mileage rules
  const yearRules = useMemo(() => (rules || []).filter(r => r.targetFactor === 'year'), [rules])
  const mileageRules = useMemo(() => (rules || []).filter(r => r.targetFactor === 'mileage'), [rules])
  const years = useMemo(() => getYearRange(yearRules), [yearRules])
  const minPrice = formula.minPrice || 0
  const maxPrice = formula.maxPrice || 100000
  const wholesaleDiscount = formula.wholesaleDiscount || 0

  // Historical sales (sold or sold elsewhere)
  const historicalSales = useMemo(() =>
    matchingVehicles.filter(v => {
      if (v.id && excludedVehicleIds.includes(v.id)) return false
      if (!((v.status === 'Sold' || v.status === 'Sold elsewhere') && v.year != null && v.miles != null && v.optic_list_price)) return false
      if (dateRange?.start || dateRange?.end) {
        const dateStr = v.sold_date || v.date_added
        if (dateStr) {
          const date = new Date(dateStr)
          if (dateRange.start && date < dateRange.start) return false
          if (dateRange.end && date > dateRange.end) return false
        }
      }
      return true
    }),
    [matchingVehicles, excludedVehicleIds, dateRange]
  )

  // Enhanced historical sales with pricing analysis
  const enhancedHistoricalSales = useMemo(() => {
    if (!historicalSales.length || !rules?.length) return [];
    
    return historicalSales.map(vehicle => {
      if (!vehicle.year || !vehicle.miles || !vehicle.optic_list_price) {
        return {
          vehicle,
          graybookPrice: 0,
          graybookWholesalePrice: 0,
          retailPriceDifference: 0,
          retailPercentDifference: 0,
          wholesalePriceDifference: 0,
          wholesalePercentDifference: 0,
          // Legacy fields for backward compatibility
          priceDifference: 0,
          percentDifference: 0
        };
      }
      
      const { price: graybookPrice } = calculatePrice({
        input: { year: vehicle.year, mileage: vehicle.miles },
        formula,
        rules: rules || [],
      });
      
      // Calculate wholesale price for graybook only
      const wholesaleDiscount = formula.wholesaleDiscount || 0.0;
      const graybookWholesalePrice = graybookPrice * (1 - (wholesaleDiscount / 100));
      
      // Calculate retail price differences (using sold price - optic_list_price)
      const retailPriceDifference = vehicle.optic_list_price - graybookPrice;
      const retailPercentDifference = graybookPrice > 0 ? (retailPriceDifference / graybookPrice) * 100 : 0;

      // Calculate wholesale price differences (using buy price - seller_asking_price)
      const buyPrice = vehicle.seller_asking_price || 0;
      const wholesalePriceDifference = buyPrice - graybookWholesalePrice;
      const wholesalePercentDifference = graybookWholesalePrice > 0 
        ? (wholesalePriceDifference / graybookWholesalePrice) * 100 
        : 0;
      
      return {
        vehicle,
        graybookPrice,
        graybookWholesalePrice,
        retailPriceDifference,
        retailPercentDifference,
        wholesalePriceDifference,
        wholesalePercentDifference,
        // Legacy fields for backward compatibility - default to retail
        priceDifference: retailPriceDifference,
        percentDifference: retailPercentDifference
      };
    });
  }, [historicalSales, rules, formula]);

  // Note: Statistics calculations have been moved to useFormulaStats hook to avoid duplication
  // Components should use useFormulaStat/useFormulaStats for metrics like avgErrorPercent, counts, etc.
  // Simple aggregations can be calculated directly from enhancedHistoricalSales if needed

  // Percent difference grid and improvements
  const percentDifferenceGrid = useMemo(() => {
    // Map of cell: { yearIdx, mileageIdx, avgSold, graybookPrice, percentDiff }
    const grid: Array<{
      yearIdx: number
      mileageIdx: number
      avgSold: number
      graybookPrice: number
      percentDiff: number
      count: number
    }> = []
    // Build a map of (yearIdx, mileageIdx) -> [sold prices]
    const salesGrid: Record<string, number[]> = {}
    historicalSales.forEach(v => {
      if (v.year == null) return
      const yearIdx = years.indexOf(v.year)
      if (yearIdx === -1) return
      const mileageIdx = mileageGrid.reduce((prev, curr, idx) => Math.abs(curr - (v.miles || 0)) < Math.abs(mileageGrid[prev] - (v.miles || 0)) ? idx : prev, 0)
      const key = `${yearIdx}-${mileageIdx}`
      if (!salesGrid[key]) salesGrid[key] = []
      salesGrid[key].push(v.optic_list_price!)
    })
    for (let yearIdx = 0; yearIdx < years.length; yearIdx++) {
      for (let mileageIdx = 0; mileageIdx < mileageGrid.length; mileageIdx++) {
        const key = `${yearIdx}-${mileageIdx}`
        const sales = salesGrid[key]
        if (!sales || sales.length === 0) continue
        const avgSold = sales.reduce((a, b) => a + b, 0) / sales.length
        const { price: graybookPrice } = calculatePrice({
          input: { year: years[yearIdx], mileage: mileageGrid[mileageIdx] },
          formula,
          rules: rules || [],
        })
        const percentDiff = ((avgSold - graybookPrice) / graybookPrice) * 100
        grid.push({ yearIdx, mileageIdx, avgSold, graybookPrice, percentDiff, count: sales.length })
      }
    }
    return grid
  }, [historicalSales, years, rules, formula])

  // Note: "improvements" (optimization suggestions) are calculated in useFormulaStats
  // via optimizationResult.improvement and hasImprovement fields

  return {
    rules,
    yearRules,
    mileageRules,
    isLoading: isLoadingRules || isLoadingVehicles,
    vehicles: matchingVehicles,
    percentDifferenceGrid,
    historicalSales,
    enhancedHistoricalSales,
    minPrice,
    maxPrice,
    wholesaleDiscount,
    ...restRules,
  }
} 
